/**
 * 标准相切配合对齐辅助函数。
 *
 * 文件职责：
 * 1. 集中定义 Tangent Mate 在存在两个相切侧时的符号约定；
 * 2. 让求解器和最终残差校验共享同一套 SOLIDWORKS 对齐语义；
 * 3. 保持输入输出为纯数据，不依赖 UI、文档状态或 Three.js 场景对象。
 */

import {
  MateAlignment,
  type AssemblyMateDefinition,
} from '../../definition';

/**
 * 根据 SOLIDWORKS Mate alignment 解析平面-圆形实体相切的有符号距离。
 *
 * 设计说明：
 * - 平面-圆柱 / 平面-圆边 / 平面-球 Tangent 都存在两个可行相切侧；
 * - SOLIDWORKS 的“同向/反向”用于在这两个侧之间切换，不能继续沿用“离当前位置最近”的策略；
 * - 这里统一以“圆形实体参考点相对平面法向的有符号距离”为约定，使求解、预览和最终校验一致。
 *
 * @param mate 当前 Mate 定义。
 * @param radius 圆形实体相切半径。
 * @returns 带符号的目标相切距离。
 */
export function resolveAlignedPlaneRoundTangentDistance(
  mate: AssemblyMateDefinition,
  radius: number,
): number {
  return mate.alignment === MateAlignment.AntiAligned
    ? -Math.abs(radius)
    : Math.abs(radius);
}

/**
 * 根据 SOLIDWORKS Mate alignment 解析两个圆形实体之间的相切中心距。
 *
 * 设计说明：
 * - 圆柱-圆柱、圆柱-球、球-球等组合存在外切与内切两类稳定解；
 * - Aligned 对应常规外切，目标距离为两个半径之和；
 * - Anti-Aligned 对应内切，目标距离为两个半径之差的绝对值；
 * - 直线 / 基准轴在相切语义中可作为零半径轴线参与，因此半径为 0 时该公式仍然成立。
 *
 * @param mate 当前 Mate 定义。
 * @param firstRadius 第一圆形实体半径。
 * @param secondRadius 第二圆形实体半径。
 * @returns 两个圆形参考中心或轴线之间的目标相切距离。
 */
export function resolveAlignedRoundRoundTangentDistance(
  mate: AssemblyMateDefinition,
  firstRadius: number,
  secondRadius: number,
): number {
  const normalizedFirstRadius = Math.abs(firstRadius);
  const normalizedSecondRadius = Math.abs(secondRadius);
  return mate.alignment === MateAlignment.AntiAligned
    ? Math.abs(normalizedFirstRadius - normalizedSecondRadius)
    : normalizedFirstRadius + normalizedSecondRadius;
}

/**
 * 判断圆形实体反向相切分支是否退化为重合。
 *
 * 设计说明：
 * SOLIDWORKS 的 Tangent Mate 约束要求两个实体在点或线处相切。
 * 对两个圆形实体而言，Anti-Aligned 分支当前代表内切半径差；当两个半径相等时，
 * 半径差为 0 会把球心或轴线压到重合位置，此时曲面会完全重合或穿叠，不再是相切。
 * 该判断必须由求解器和最终校验共同复用，避免预览阶段拒绝但重载校验阶段又误判为已满足。
 *
 * @param mate 当前 Tangent Mate 定义。
 * @param firstRadius 第一个圆形实体半径。
 * @param secondRadius 第二个圆形实体半径。
 * @param epsilon 长度容差。
 * @returns 是否属于等半径内切退化。
 */
export function isRoundRoundTangentInternalBranchDegenerate(
  mate: AssemblyMateDefinition,
  firstRadius: number,
  secondRadius: number,
  epsilon: number,
): boolean {
  return mate.alignment === MateAlignment.AntiAligned
    && Math.abs(Math.abs(firstRadius) - Math.abs(secondRadius)) <= epsilon;
}
